/**
 * @file SelectClinicView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Button, FormControlLabel, Grid, List, ListItem, Radio, Typography, InputAdornment, Snackbar, Dialog, DialogContent } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import "./../Login.css"
import { SelectClinicLabelTheme, SignButtonTheme, SsoLabelTheme } from "../LoginTheme";
import { GridSearchIcon } from "@mui/x-data-grid";
import { Clinic } from "../../../services/models/Clinic";
import { TextFieldWrapper } from "../../common/customviews/TextFieldWrapper";
import { CSSTransition } from "react-transition-group";
import { AppAlert } from "../../utils/AppAlert";
import { BaseClinicComponent, ClinicInfo } from "../../home/base/BaseClinicComponent";
import { NewClinicView } from "../../home/admin/views/NewClinicView";
import { NoDataFound } from "../../common/customviews/NoDataFound";
import { NoClinicFound } from "../../utils/AppConstant";
import { CustomToolTip } from "../../common/customviews/CustomToolTip";

/**
 * A type of Component state
 */
type CompState = {
    filteredClinicList: ClinicInfo[], errorToggle : boolean, newClinicDialog: boolean
}

/**
 * An interface definition for Component Props
 */
interface Props {
    onClinicSelected: any
}

/**
 * A class definition for SelectClinicView
 */
export class SelectClinicView extends BaseClinicComponent<Props, CompState> {

    constructor(props: Props) {
        super(props)

        const clinics: Clinic[] = JSON.parse(this.getAllClinics());
        this.logger.verbose(`SelectClinicView() - ${JSON.stringify(clinics)}`, SelectClinicView.name);
        
        this.modifyClinic(clinics);

        this.state = {
            filteredClinicList: this.filteredClinicList,
            errorToggle : false, newClinicDialog: false
        }
    }

    /**
     * Updates
     */
    private updateClinicList = (clinic: Clinic) =>{
        const clinics: Clinic[] = JSON.parse(this.getAllClinics());
        clinics.push(clinic);
        this.setAllClinics(clinics);
    }

    /**
     * Calls on changes of search text
     * @param searchText string
     * @returns 
     */    
    private onChangeSearchText = (searchText: string) => {
        if (searchText.trim().length < 2) {
            this.filteredClinicList = this.clinicList;
            this.setClinicLists(this.clinicList);
            return;
        }
        const searchedClinicList: ClinicInfo[] = [];
        this.clinicList.forEach((clinic: ClinicInfo) => {
            if (clinic.name.toLowerCase().includes(searchText.trim().toLowerCase())) { searchedClinicList.push(clinic); }
        });

        this.filteredClinicList = searchedClinicList;
        this.setClinicLists(this.filteredClinicList);
    }

    /**
     * Sets clinic lists
     * @param clinicList ClinicInfo[]
     */    
    private setClinicLists = (clinicList: ClinicInfo[]) => {
        this.setState({
            filteredClinicList: clinicList
        });
    }

    /**
     * Shows new clinic dialog
     */
    private showNewClinicDialog = ()=>{
        this.setState({
            newClinicDialog: true
        });
    }

    /**
     * Dismisses new clinic dialog
     */
    private dismissNewClinicDialog = ()=>{
        this.setState({
            newClinicDialog: false
        });
    }
    /**
     * Goes to dashboard
     */    
    private gotoDashBoard = () => {
        let selectedClinic: ClinicInfo | undefined;
        this.filteredClinicList.forEach((clinic: ClinicInfo) => {
            if (clinic.isSelected) {
                selectedClinic = clinic;
            }
        });
        if (selectedClinic) { 
            this.props.onClinicSelected(selectedClinic)
         } else {
            this.showError();
         }
    }

    /**
     * On change selection
     * @param id string
     */
    private onChangeSelection = (id: string) => {
        let index = 0;
        this.filteredClinicList.forEach((clinic: ClinicInfo) => {
            clinic.isSelected = false
            if (clinic.id === id) { clinic.isSelected = true }
            this.filteredClinicList[index] = clinic; ++index;

        });
        this.setClinicLists(this.filteredClinicList);
    }

    /**
     * Handles new clinic callback
     */
    private onNewClinicCreated = (clinic: Clinic)=> {
        this.updateClinicList(clinic);
        this.props.onClinicSelected(clinic)
    }

    /**
     * Hides error
     */
    private hideError = () => {
        this.setState({ errorToggle: false })
    }

    /**
     * Shows error
     */
    private showError = () => {
        this.setState({ errorToggle: true })
    }

    render() {
        return <React.Fragment>
            <div className="Clinic">
                <ThemeProvider theme={SelectClinicLabelTheme}>
                    {this.clinicList.length > 0 &&<Typography className="SelectClinicLabel">Select a clinic</Typography>}
                </ThemeProvider>
                {this.clinicList.length > 3 && <TextFieldWrapper
                    className="SearchTextField"
                    type="search"
                    label="Search by name"
                    // value={searchTerm}
                    onChange={(e) => { this.onChangeSearchText(e.target.value) }}
                    sx={{ width: "100%" }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <GridSearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />} 
                {
                 this.clinicList.length <= 0 && 
                 <div className="ClinicListHeight"><NoDataFound message={NoClinicFound} /> </div>
                }
                {
                this.clinicList.length > 0 && <List aria-labelledby="decorated-list-demo" className="ClinicListHeight">
                    {this.state.filteredClinicList.map((clinic: ClinicInfo) => (
                        <ListItem style={{ height: "50px" }} key={clinic.id}>
                            <FormControlLabel style={{ width: "100%" }}
                                value={clinic.isSelected}    //Pass this
                                // eslint-disable-next-line
                                onChange={(e) => { this.onChangeSelection(clinic.id) }} checked={clinic.isSelected}   //Also this
                                labelPlacement="end"
                                control={<Radio name={clinic.name} sx={{
                                    color: "#173166",
                                    "&.Mui-checked": {
                                        color: "#086A96",
                                    },
                                }} />}
                                label={
                                    <ThemeProvider theme={SsoLabelTheme}>
                                        <CustomToolTip title={clinic.name as string} toolTipLengthLimit={25}>
                                            <Typography className="RadioItem" sx={{ textTransform: "capitalize"}}>{clinic.name}</Typography>
                                        </CustomToolTip>
                                    </ThemeProvider>
                                }
                            />
                        </ListItem>

                    ))}
                </List>
                }

                <Grid container justifyContent="right" alignItems="center" style={{ marginTop: "20px" }}>
                    <Grid item xs={5}>
                        <ThemeProvider theme={SignButtonTheme}>
                            <Button onClick={this.showNewClinicDialog} className="AddNewClinicButton" size="small">Add New Clinic</Button>
                        </ThemeProvider>
                    </Grid>

                    <Grid item xs={5} justifyContent="center" alignItems="center" container>
                        <ThemeProvider theme={SignButtonTheme}>
                            <Button onClick={this.gotoDashBoard} className="GoToDashBoardButton" size="small" disabled={this.clinicList.length <= 0 ? true: false }>Go to DashBoard</Button>
                        </ThemeProvider>
                    </Grid>

                </Grid>
                <Dialog onClose={this.dismissNewClinicDialog} aria-labelledby="customized-dialog-title" open={this.state.newClinicDialog}
                        maxWidth="md" fullWidth  disableScrollLock={true}   PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                        <DialogContent dividers style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <NewClinicView onDialogDismissed={this.dismissNewClinicDialog} onNewClinicCreated={this.onNewClinicCreated}/>
                        </DialogContent>
                    </Dialog>
            </div>
            <CSSTransition
                in={this.state.errorToggle}
                timeout={300}
                classNames="alert"
                unmountOnExit>
                <Snackbar open={this.state.errorToggle} autoHideDuration={6000} onClose={this.hideError}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                    <AppAlert onClose={this.hideError} severity="warning" sx={{ width: "150%" }}>
                        {`Kindly select a clinic.`}
                    </AppAlert>
                </Snackbar>
            </CSSTransition>
        </React.Fragment >
    }
}
